// Live multi-group rounds on completetourneylive — shared by GGC website + app.
// GROUP PLAY is the frame; the GAME TYPE(s) are a selectable choice (Skins, Scats,
// Stroke, Match Play, Four Ball, Scramble, Shamble, Practice) — settled per whatever
// is chosen, net or gross per the Use Handicap master switch. No typed code: the
// round id lives in QR/links only. Two-level QR chain + per-group scorekeeper roles
// are unchanged (RTDB key stays "matches" so the deployed rules keep working; a
// "match" here is a GROUP of players who score together).
import { ref, set, update, get, onValue, remove } from 'firebase/database'
import { memberDb } from './memberBackend.js'
import { getMemberId } from './memberGgc.js'
import { calcCourseHandicapRaw, calcPlayingHandicap, strokesOnHole, DEFAULT_STROKE_INDEX } from '../admin/gg/ggHandicap.js'

const TTL_MS = 24 * 60 * 60 * 1000
const genId = () => 'r' + Math.random().toString(36).slice(2, 8)

export function expandPlayer(p, course, tee) {
  const idx = Number(p.handicapIndex)
  const chRaw = Number.isFinite(idx) ? calcCourseHandicapRaw({ handicapIndex: idx, slopeRating: tee.slope, courseRating: tee.rating, par: course.par }) : 0
  const ph = calcPlayingHandicap(chRaw, Number(p.allowance) || 100)
  const strokesPerHole = Array.from({ length: 18 }, (_, h) => strokesOnHole(ph, h, course.strokeIndex || DEFAULT_STROKE_INDEX))
  return { ...p, courseHandicap: Math.round(chRaw), playingHandicap: ph, strokesPerHole }
}

// players carry { id,name,ghin,handicapIndex,allowance, group(1..N), team('A'|'B') } + CH/PH/strokes.
export async function createMasterRound({ name, course, games, useHandicap, gameOpts, players, groupCount, noBets }) {
  const uid = getMemberId()
  if (!uid) throw new Error('not signed in')
  const now = Date.now()
  const id = genId()
  const matches = {}
  for (let g = 1; g <= (Number(groupCount) || 1); g++) {
    const ids = players.filter((p) => (Number(p.group) || 1) === g).map((p) => p.id)
    if (!ids.length) continue
    matches[`m${g}`] = { matchNumber: g, scorekeeperId: '', scorekeeperName: '', playerIds: ids, scores: {}, greenies: {}, complete: false, updatedAt: now }
  }
  const round = {
    organizerId: uid, name: name || 'Group Round', createdAt: now, expiresAt: now + TTL_MS, status: 'active',
    course, games: games && games.length ? games : ['stroke'], useHandicap: !!useHandicap, gameOpts: gameOpts || {},
    noBets: !!noBets,
    players, matches,
  }
  await set(ref(memberDb, `masterRounds/${id}`), stripUndef(round))
  return { id }
}

export async function getMasterRound(id) {
  if (!id) return null
  const snap = await get(ref(memberDb, `masterRounds/${id}`))
  const r = snap.val()
  if (!r || (r.expiresAt || 0) < Date.now()) return null
  return r
}
export function subscribeMasterRound(id, cb) {
  if (!id) { cb(null); return () => {} }
  return onValue(ref(memberDb, `masterRounds/${id}`), (s) => cb(s.val() || null), () => cb(null))
}
export async function claimMatch(id, mid) {
  const uid = getMemberId()
  if (!uid) throw new Error('sign in as a member to score')
  await update(ref(memberDb, `masterRounds/${id}/matches/${mid}`), { scorekeeperId: String(uid), scorekeeperName: `Member ${uid}`, updatedAt: Date.now() })
}
export async function setMatchScore(id, mid, hole, pid, gross) {
  await update(ref(memberDb, `masterRounds/${id}/matches/${mid}`), { [`scores/${hole}/${pid}`]: gross === null || gross === '' ? null : Number(gross), updatedAt: Date.now() })
}
export async function finalizeMatch(id, mid) {
  await update(ref(memberDb, `masterRounds/${id}/matches/${mid}`), { complete: true, completedAt: Date.now(), updatedAt: Date.now() })
}
export async function deleteMasterRound(id) { await remove(ref(memberDb, `masterRounds/${id}`)) }

export function myRole(round) {
  const uid = getMemberId()
  if (!round || !uid) return { organizer: false, scorekeeperOf: null, uid }
  const organizer = String(round.organizerId) === String(uid)
  let scorekeeperOf = null
  for (const [mid, m] of Object.entries(round.matches || {})) if (String(m.scorekeeperId) === String(uid)) scorekeeperOf = mid
  return { organizer, scorekeeperOf, uid }
}

function stripUndef(v) {
  if (Array.isArray(v)) return v.map(stripUndef)
  if (v && typeof v === 'object') { const o = {}; for (const [k, val] of Object.entries(v)) if (val !== undefined) o[k] = stripUndef(val); return o }
  return v
}
